/**
 * Time Entry Template Constants
 * Centralized template definitions for time entry form.
 *
 * Templates are split by category group across multiple files. This barrel
 * concatenates them in their original order and exposes the same public API
 * the legacy `time-entry-templates.ts` file did.
 */

import type { TimeEntryTemplateData } from "./types";
import { WORK_TEMPLATES } from "./work-templates";
import { INFRASTRUCTURE_TEMPLATES } from "./infrastructure-templates";
import { OPERATIONS_TEMPLATES } from "./operations-templates";

export type { TimeEntryTemplateFields, TimeEntryTemplateData } from "./types";

/**
 * All available time entry templates.
 *
 * NOTE: order matches the original monolithic file so any consumers that rely
 * on positional iteration continue to see identical output. The split files
 * each interleave categories where the original list interleaved them.
 */
export const TIME_ENTRY_TEMPLATES = [
  // Work / engagement templates (development, meetings, support, etc.)
  WORK_TEMPLATES[0], // development
  WORK_TEMPLATES[1], // client-meeting
  WORK_TEMPLATES[2], // support-call
  WORK_TEMPLATES[3], // training
  WORK_TEMPLATES[4], // research
  WORK_TEMPLATES[5], // documentation
  WORK_TEMPLATES[6], // internal-meeting
  WORK_TEMPLATES[7], // admin-work
  INFRASTRUCTURE_TEMPLATES[0], // vendor-followup
  INFRASTRUCTURE_TEMPLATES[1], // cloud-setup-azure
  INFRASTRUCTURE_TEMPLATES[2], // cloud-setup-aws
  WORK_TEMPLATES[8], // client-progress-review
  INFRASTRUCTURE_TEMPLATES[3], // ticket-investigation
  INFRASTRUCTURE_TEMPLATES[4], // system-monitoring
  INFRASTRUCTURE_TEMPLATES[5], // backup-verification
  INFRASTRUCTURE_TEMPLATES[6], // security-patch
  INFRASTRUCTURE_TEMPLATES[7], // network-troubleshooting
  INFRASTRUCTURE_TEMPLATES[8], // email-configuration
  INFRASTRUCTURE_TEMPLATES[9], // phone-system-config
  OPERATIONS_TEMPLATES[0], // software-installation
  OPERATIONS_TEMPLATES[1], // hardware-setup
  WORK_TEMPLATES[9], // user-training
  INFRASTRUCTURE_TEMPLATES[10], // incident-response
  INFRASTRUCTURE_TEMPLATES[11], // compliance-review
  OPERATIONS_TEMPLATES[2], // project-planning
  OPERATIONS_TEMPLATES[3], // travel-time
  OPERATIONS_TEMPLATES[4], // on-site-visit
  OPERATIONS_TEMPLATES[5], // remote-support
  OPERATIONS_TEMPLATES[6], // system-maintenance
  OPERATIONS_TEMPLATES[7], // vendor-management
  OPERATIONS_TEMPLATES[8], // capacity-planning
  OPERATIONS_TEMPLATES[9], // disaster-recovery
] as const satisfies readonly TimeEntryTemplateData[];

/**
 * Type for template IDs (discriminated union of all template IDs)
 */
export type TimeEntryTemplateId = typeof TIME_ENTRY_TEMPLATES[number]["id"];

/**
 * Find a template by ID with type safety
 */
export function findTemplateById(id: string): TimeEntryTemplateData | undefined {
  return TIME_ENTRY_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get all unique categories from templates
 */
export function getTemplateCategories(): string[] {
  return Array.from(new Set(TIME_ENTRY_TEMPLATES.map((t) => t.category)));
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: string): TimeEntryTemplateData[] {
  return TIME_ENTRY_TEMPLATES.filter((t) => t.category === category);
}
